import React, { useState, useEffect, useRef, useContext } from "react";
import CountryDropdown from "../../components/elements/CountryDropdown";
import { Link, useNavigate } from "react-router-dom";
import { SupportRequestLink } from "../../components/elements/SupportRequestLink";
import {
  Input,
  Space,
  Typography,
  Table,
  Button,
  Drawer,
  Popover,
  message,
  Modal,
  Switch,
} from "antd";
import { AuthContext } from "../../services/auth.service";
import { SearchOutlined } from "@ant-design/icons";
import "./styles.scss";
import localStorageService from "../../services/localStorage.service";
import ConfigService from "../../services/config.service";
const { Text } = Typography;
const modalHeaderName = "settings";

// Email validation helper function
const isValidEmail = (email) => {
  if (!email || email.length === 0) return false;
  // RFC 5322 compliant email regex with TLD validation
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
  return emailRegex.test(email.trim());
};

const isValidInput = (input) => {
  if (!input || input.trim().length === 0) return false;

  return true
}

// Side menu content for Profile Details
export const ProfileDetails = ({ context }) => {
  const {
    firstName,
    lastName,
    email,
    companyId,
    setModalSaveStatus,
    setConfigParams,
    navigate,
  } = useContext(context);

  const UserManagementDocsLink = `https://docs.code-cube.io/tag-monitor/configuration/tag-volume/`;
  const initialFirstName = useRef(firstName);
  const initialLastName = useRef(lastName);
  const initialEmail = useRef(email);

  const [curFirstName, setCurFirstName] = useState(firstName);
  const [curLastName, setCurLastName] = useState(lastName);
  const [curEmail, setCurEmail] = useState(email);

  const [firstNameStatus, setFirstNameStatus] = useState(isValidInput(firstName));
  const [lastNameStatus, setLastNameStatus] = useState(true); // Not required
  const [emailStatus, setEmailStatus] = useState(isValidEmail(email));

  const [saveStatus, setSaveStatus] = useState(false);
  const [configParamsToSave, setConfigParamsToSave] = useState({});
  const { setDisableSaveButton } = useContext(context);
  const checkSaveStatus = () => {
    setSaveStatus(
      isValidInput(curFirstName) &&
        isValidInput(curLastName) &&
        isValidEmail(curEmail) &&
        (curFirstName !== initialFirstName.current ||
          curLastName !== initialLastName.current ||
          curEmail !== initialEmail.current),
    );
  };

  useEffect(() => {
    setCurFirstName(firstName);
    setCurLastName(lastName);
    setCurEmail(email);
  }, [firstName, lastName, email]);

  useEffect(() => {
    setDisableSaveButton(
      !isValidInput(curFirstName) || !isValidEmail(curEmail) ? true : false,
    );

    setFirstNameStatus(isValidInput(curFirstName));
    setLastNameStatus(true); // Not required
    setEmailStatus(isValidEmail(curEmail));

    checkSaveStatus();

    const newObject = { ...configParamsToSave };
    const userProfile = {
      first_name: curFirstName,
      last_name: curLastName,
      email: curEmail,
    };
    newObject["user_profile"] = userProfile;

    setConfigParamsToSave(newObject);
  }, [curFirstName, curLastName, curEmail]);

  useEffect(() => setSaveStatus(false), []);

  useEffect(() => setModalSaveStatus(saveStatus), [saveStatus]);

  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

  return (
    <Space
      key={companyId}
      direction="vertical"
      size="large"
      style={{ padding: "1rem 1.8rem 0 .25rem" }}
    >
      <Space direction="vertical" size="small" style={{ rowGap: "30px" }}>
        <Space
          direction="vertical"
          size="small"
          className="profile-details-section-header"
        >
          <h2 className="header-letter-spacing">Profile</h2>
          <p>
            These are your personal details linked to your Code-Cube.io account.
            You can update them at any time.
          </p>
        </Space>
        <Space direction="vertical" size="large" style={{ rowGap: "1rem" }}>
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space
              direction="vertical"
              size="small"
              style={{ flex: 1, gap: "16px" }}
            >
              <span>
                <Text strong style={{ padding: 0 }}>
                  First name
                </Text>{" "}
                *
              </span>
              <Input
                value={curFirstName}
                onChange={(e) => setCurFirstName(e.target.value)}
                status={firstNameStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
            <Space
              direction="vertical"
              size="small"
              style={{ flex: 1, gap: "16px" }}
            >
              <Text strong style={{ padding: 0 }}>
                Last name
              </Text>
              <Input
                value={curLastName}
                onChange={(e) => setCurLastName(e.target.value)}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>
          <Space direction="vertical" size="small" style={{ gap: 0 }}>
            <span>
              <Text strong style={{ padding: 0 }}>
                Email
              </Text>{" "}
              *
            </span>
            <Text>
              Update your email address below. Re-login required after the
              change.
            </Text>
            <Space.Compact
              size="middle"
              style={{ width: "100%", marginTop: "16px" }}
            >
              <Input
                placeholder="hello@code-cube.io"
                value={curEmail}
                type="email"
                onChange={(e) => setCurEmail(e.target.value)}
                style={{ width: "19rem" }}
              />
            </Space.Compact>
          </Space>
        </Space>
        <Space direction="vertical" size="small" style={{ gap: 0 }}>
          <Text strong>Password</Text>
          <Text>
            Reset your password below by clicking the button and following the
            instructions.
          </Text>
          <Button
            style={{ marginTop: "16px" }}
            type="default"
            onClick={() => navigate("/password-reset")}
          >
            reset password
          </Button>
        </Space>
      </Space>
    </Space>
  );
};

// Side menu content for Company Details
export const CompanyDetails = ({ context }) => {
  const {
    companyName,
    chamberOfCommerceNumber,
    vatNumber,
    moneybirdCid,
    billingStreet1,
    billingStreet2,
    billingPostcode,
    billingProvince,
    billingCity,
    billingCountry,
    billingFullName,
    billingEmail,
    invoiceRefNumber,
    companyId,
    companyActive,
    setCompanyActive,
    setModalSaveStatus,
    setConfigParams,
  } = useContext(context);

  // Admin-only company active toggle (acts immediately, independent of the form save).
  const { adminUser } = useContext(AuthContext);
  const [activeToggleLoading, setActiveToggleLoading] = useState(false);
  const [localActive, setLocalActive] = useState(companyActive);

  useEffect(() => {
    setLocalActive(companyActive);
  }, [companyActive]);

  const handleToggleCompanyActive = async (checked) => {
    setActiveToggleLoading(true);
    setLocalActive(checked);
    try {
      const resp = await ConfigService.toggleCompanyActive({
        companyId,
        active: checked,
      });
      const respData = (await resp?.json()) || resp;
      if (respData?.result === true) {
        setCompanyActive(checked);
        message.success(
          `Company set to ${checked ? "active" : "inactive"}.`,
        );
      } else {
        setLocalActive(!checked);
        message.error(
          respData?.error || "Failed to update company status. Please try again.",
        );
      }
    } catch (err) {
      setLocalActive(!checked);
      message.error("Failed to update company status. Please try again.");
    } finally {
      setActiveToggleLoading(false);
    }
  };

  const initialCompanyName = useRef(companyName);
  const initialChamberOfCommerceNumber = useRef(chamberOfCommerceNumber);
  const initialVatNumber = useRef(vatNumber);
  const initialMoneybirdCid = useRef(moneybirdCid);
  const initialBillingStreet1 = useRef(billingStreet1);
  const initialBillingStreet2 = useRef(billingStreet2);
  const initialBillingPostcode = useRef(billingPostcode);
  const initialBillingProvince = useRef(billingProvince);
  const initialBillingCity = useRef(billingCity);
  const initialBillingCountry = useRef(billingCountry);
  const initialBillingFullName = useRef(billingFullName);
  const initialBillingEmail = useRef(billingEmail);
  const initialInvoiceRefNumber = useRef(invoiceRefNumber);

  const [curCompanyName, setCurCompanyName] = useState(companyName);
  const [curChamberOfCommerceNumber, setCurChamberOfCommerceNumber] = useState(
    chamberOfCommerceNumber,
  );
  const [curVatNumber, setCurVatNumber] = useState(vatNumber);
  const [curBillingStreet1, setCurBillingStreet1] = useState(billingStreet1);
  const [curBillingStreet2, setCurBillingStreet2] = useState(billingStreet2);
  const [curBillingPostcode, setCurBillingPostcode] = useState(billingPostcode);
  const [curBillingProvince, setCurBillingProvince] = useState(billingProvince);
  const [curBillingCity, setCurBillingCity] = useState(billingCity);
  const [curBillingCountry, setCurBillingCountry] = useState(billingCountry);
  const [curBillingFullName, setCurBillingFullName] = useState(billingFullName);
  const [curBillingEmail, setCurBillingEmail] = useState(billingEmail);
  const [curInvoiceRefNumber, setCurInvoiceRefNumber] =
    useState(invoiceRefNumber);
  const { setDisableSaveButton } = useContext(context);
  const [companyNameStatus, setCompanyNameStatus] = useState(
    isValidInput(companyName)
  );
  const [chamberOfCommerceStatus, setChamberOfCommerceStatus] = useState(
    isValidInput(chamberOfCommerceNumber)
  );
  const [billingStreet1Status, setBillingStreet1Status] = useState(
    isValidInput(billingStreet1)
  );
  const [billingPostcodeStatus, setBillingPostcodeStatus] = useState(
    isValidInput(billingPostcode)
  );
  const [billingCityStatus, setBillingCityStatus] = useState(
    isValidInput(billingCity)
  );
  const [billingCountryStatus, setBillingCountryStatus] = useState(
    isValidInput(billingCountry)
  );
  const [fullNameStatus, setFullNameStatus] = useState(
    isValidInput(billingFullName)
  );
  const [emailStatus, setEmailStatus] = useState(isValidEmail(billingEmail));

  const [saveStatus, setSaveStatus] = useState(false);
  const [configParamsToSave, setConfigParamsToSave] = useState({});

  const checkSaveStatus = () => {
    const requiredFieldsFilled =
      isValidInput(curCompanyName) &&
      isValidInput(curChamberOfCommerceNumber) &&
      isValidInput(curBillingStreet1) &&
      isValidInput(curBillingPostcode) &&
      isValidInput(curBillingCity) &&
      isValidInput(curBillingCountry) &&
      isValidInput(curBillingFullName) &&
      isValidEmail(curBillingEmail)

    const anyFieldChanged =
      curCompanyName !== initialCompanyName.current ||
      curChamberOfCommerceNumber !== initialChamberOfCommerceNumber.current ||
      curVatNumber !== initialVatNumber.current ||
      curBillingStreet1 !== initialBillingStreet1.current ||
      curBillingStreet2 !== initialBillingStreet2.current ||
      curBillingPostcode !== initialBillingPostcode.current ||
      curBillingProvince !== initialBillingProvince.current ||
      curBillingCity !== initialBillingCity.current ||
      curBillingCountry !== initialBillingCountry.current ||
      curBillingFullName !== initialBillingFullName.current ||
      curBillingEmail !== initialBillingEmail.current ||
      curInvoiceRefNumber !== initialInvoiceRefNumber.current;

    setSaveStatus(
      anyFieldChanged ? (requiredFieldsFilled ? true : false) : false,
    );
  };

  useEffect(() => {
    setCurCompanyName(companyName);
    setCurChamberOfCommerceNumber(chamberOfCommerceNumber);
    setCurVatNumber(vatNumber);
    setCurBillingStreet1(billingStreet1);
    setCurBillingStreet2(billingStreet2);
    setCurBillingPostcode(billingPostcode);
    setCurBillingProvince(billingProvince);
    setCurBillingCity(billingCity);
    setCurBillingCountry(billingCountry);
    setCurBillingFullName(billingFullName);
    setCurBillingEmail(billingEmail);
    setCurInvoiceRefNumber(invoiceRefNumber);
  }, [
    companyName,
    chamberOfCommerceNumber,
    vatNumber,
    moneybirdCid,
    billingStreet1,
    billingStreet2,
    billingPostcode,
    billingProvince,
    billingCity,
    billingCountry,
    billingFullName,
    billingEmail,
    invoiceRefNumber,
  ]);

  useEffect(() => {
    setCompanyNameStatus(isValidInput(curCompanyName));
    setChamberOfCommerceStatus(isValidInput(curChamberOfCommerceNumber));
    setBillingStreet1Status(isValidInput(curBillingStreet1));
    setBillingPostcodeStatus(isValidInput(curBillingPostcode));
    setBillingCityStatus(isValidInput(curBillingCity));
    setBillingCountryStatus(isValidInput(curBillingCountry));
    setFullNameStatus(isValidInput(curBillingFullName));
    setEmailStatus(isValidEmail(curBillingEmail));
    setDisableSaveButton(
      !isValidInput(curCompanyName) ||
        !isValidInput(curChamberOfCommerceNumber) ||
        !isValidInput(curBillingStreet1) ||
        !isValidInput(curBillingPostcode) ||
        !isValidInput(curBillingCity) ||
        !isValidInput(curBillingCountry) ||
        !isValidInput(curBillingFullName) ||
        !isValidEmail(curBillingEmail)
        ? true
        : false,
    );

    checkSaveStatus();

    const newObject = { ...configParamsToSave };
    const companyProfile = {
      display_company_name: curCompanyName,
      company_cocn_number: curChamberOfCommerceNumber,
      company_vat_number: curVatNumber,
      billing_street_address_1: curBillingStreet1,
      billing_street_address_2: curBillingStreet2,
      billing_postal_code: curBillingPostcode,
      billing_province: curBillingProvince,
      billing_city: curBillingCity,
      billing_country: curBillingCountry,
      billing_fullname: curBillingFullName,
      billing_email: curBillingEmail,
      company_invoice_ref_number: curInvoiceRefNumber,
    };

    newObject["company_profile"] = companyProfile;

    setConfigParamsToSave(newObject);
  }, [
    curCompanyName,
    curChamberOfCommerceNumber,
    curVatNumber,
    curBillingStreet1,
    curBillingStreet2,
    curBillingPostcode,
    curBillingProvince,
    curBillingCity,
    curBillingCountry,
    curBillingFullName,
    curBillingEmail,
    curInvoiceRefNumber,
  ]);

  useEffect(() => setSaveStatus(false), []);

  useEffect(() => setModalSaveStatus(saveStatus), [saveStatus]);

  useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);
  return (
    <Space
      key={companyId}
      direction="vertical"
      size={50}
      style={{ gap: "30px", padding: "1rem 1.8rem 0 .25rem" }}
    >
      {/* Company Profile Section */}
      <h2 className="header-letter-spacing">Company details</h2>
      {adminUser && (
        <Space direction="vertical" size="small">
          <Text strong>Company status</Text>
          <Space direction="horizontal" size="middle">
            <Switch
              checked={localActive}
              loading={activeToggleLoading}
              onChange={handleToggleCompanyActive}
            />
            <Text>{localActive ? "Active" : "Inactive"}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: "0.8rem" }}>
            Deactivating pauses this client's monitoring and updates the
            configuration bucket.
          </Text>
        </Space>
      )}
      <Space direction="vertical" size="small">
        <Space direction="vertical" size="middle" style={{ rowGap: "1rem" }}>
          {/* Company Details Row */}
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>Company name</Text> *
              </span>
              <Input
                value={curCompanyName}
                onChange={(e) => setCurCompanyName(e.target.value)}
                status={companyNameStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>Chamber of commerce</Text> *
              </span>
              <Input
                value={curChamberOfCommerceNumber}
                onChange={(e) => setCurChamberOfCommerceNumber(e.target.value)}
                status={chamberOfCommerceStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <Text strong>VAT number</Text>
              <Input
                value={curVatNumber}
                onChange={(e) => setCurVatNumber(e.target.value)}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>
          {/* </Space> */}
          {/* </Space>

          <Space direction="vertical" size="small" style={{ gap: "1rem" }}> */}
          {/* <h2 className="header-letter-spacing">Address details</h2> */}

          {/* Address Row 1 */}
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>Street address</Text> *
              </span>
              <Input
                value={curBillingStreet1}
                onChange={(e) => setCurBillingStreet1(e.target.value)}
                status={billingStreet1Status ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <Text strong>Street address 2</Text>
              <Input
                value={curBillingStreet2}
                onChange={(e) => setCurBillingStreet2(e.target.value)}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>

          {/* Address Row 2 */}
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>Postal code</Text> *
              </span>
              <Input
                value={curBillingPostcode}
                onChange={(e) => setCurBillingPostcode(e.target.value)}
                status={billingPostcodeStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>City</Text> *
              </span>
              <Input
                value={curBillingCity}
                onChange={(e) => setCurBillingCity(e.target.value)}
                status={billingCityStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>

          {/* Address Row 3 */}
          <Space
            direction="horizontal"
            size={50}
            style={{ columnGap: "1.5rem" }}
          >
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <Text strong>State / Province</Text>
              <Input
                value={curBillingProvince}
                onChange={(e) => setCurBillingProvince(e.target.value)}
                style={{ width: "19rem" }}
              />
            </Space>
            <Space direction="vertical" size="small" style={{ flex: 1 }}>
              <span>
                <Text strong>Country</Text> *
              </span>
              <CountryDropdown
                value={curBillingCountry}
                onChange={(value) => setCurBillingCountry(value)}
                status={billingCountryStatus ? "" : "error"}
                style={{ width: "19rem" }}
              />
            </Space>
          </Space>
        </Space>
      </Space>

      {/* Billing Details Section */}
      <Space direction="vertical" size="small" style={{ gap: "1rem" }}>
        <span style={{ fontSize: "16px", fontWeight: 700 }}>
          Billing details
        </span>

        <Space direction="horizontal" size={50} style={{ columnGap: "1.5rem" }}>
          <Space direction="vertical" size="small" style={{ flex: 1 }}>
            <span>
              <Text strong>Full name</Text> *
            </span>
            <Input
              value={curBillingFullName}
              onChange={(e) => setCurBillingFullName(e.target.value)}
              status={fullNameStatus ? "" : "error"}
              style={{ width: "19rem" }}
            />
          </Space>
          <Space direction="vertical" size="small" style={{ flex: 1 }}>
            <span>
              <Text strong>Email</Text> *
            </span>
            <Input
              value={curBillingEmail}
              type="email"
              onChange={(e) => setCurBillingEmail(e.target.value)}
              status={emailStatus ? "" : "error"}
              style={{ width: "19rem" }}
            />
          </Space>
        </Space>
        <Space direction="horizontal" size={50} style={{ columnGap: "1.5rem" }}>
          <Space direction="vertical" size="small" style={{ flex: 1 }}>
            <Text strong>Invoice Reference Number</Text>
            <Input
              value={curInvoiceRefNumber}
              onChange={(e) => setCurInvoiceRefNumber(e.target.value)}
              style={{ width: "19rem" }}
            />
          </Space>
        </Space>
      </Space>
    </Space>
  );
};

// Team Members Details
/**
 * TeamMembersDetails component for managing and inviting portal users.
 *
 * This component displays a list of team members, allows searching, inviting new users,
 * and deleting existing users (including self-deletion with confirmation).
 * It uses Ant Design components for UI and relies on context for team member data and actions.
 *
 * @component
 * @param {Object} props
 * @param {React.Context} props.context - The React context providing team member data and refresh function.
 *
 * @returns {JSX.Element} The rendered TeamMembersDetails component.
 *
 * @example
 * <TeamMembersDetails context={TeamMembersContext} />
 */
export const TeamMembersDetails = ({ context }) => {
  //Docs
  const UserManagementDocsLink = `https://docs.code-cube.io/user-management/invite-user/`;

  // Context
  const { teamMembers: contextTeamMembers, companyId } = useContext(context);
  const { inviteUser, deleteUser, adminUser } = useContext(AuthContext);

  // State - Table
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Get current user email
  const currentUserEmail = localStorageService.getUserData()?.email;

  // State - Invite Form
  const [curFirstName, setCurFirstName] = useState("");
  const [curLastName, setCurLastName] = useState("");
  const [curEmail, setCurEmail] = useState("");
  const [firstNameStatus, setFirstNameStatus] = useState(true);
  const [lastNameStatus, setLastNameStatus] = useState(true); // Not required
  const [emailStatus, setEmailStatus] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // State - UI
  const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  // Constants
  const defaultPlacement = "right";
  const isFormValid = curFirstName?.length > 0 && isValidEmail(curEmail);

  // Effects
  useEffect(() => {
    if (!contextTeamMembers || contextTeamMembers.length === 0) {
      setDataSource([]); // Clear the table if no team members
      return;
    }
    if (contextTeamMembers && contextTeamMembers.length > 0) {
      // First, find the current user's data
      const currentUser = contextTeamMembers.find(
        (member) => member.email === currentUserEmail,
      );

      // Filter and sort other members, excluding admin users 
      const otherMembers = contextTeamMembers
        .filter((member) => member.email !== currentUserEmail)
        .filter((member) => !member.is_admin) // Filter out admin users for all users
        .sort((a, b) => new Date(b.date_joined) - new Date(a.date_joined));

      // Format other members
      const formattedOtherMembers = otherMembers.map((member) => ({
        key: member.email,
        id: member.email,
        email: member.email,
        date_joined: member.date_joined
          ? new Date(member.date_joined).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "N/A",
        isCurrentUser: false,
      }));

      // Create final data array with current user at top
      let formattedData = [];
      if (currentUser) {
        formattedData = [
          {
            key: currentUser.email,
            id: currentUser.email,
            email: currentUser.email,
            date_joined: currentUser.date_joined
              ? new Date(currentUser.date_joined).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "N/A",
            isCurrentUser: true,
          },
          ...formattedOtherMembers,
        ];
      } else {
        formattedData = formattedOtherMembers;
      }

      setDataSource(formattedData);
    }
  }, [contextTeamMembers, currentUserEmail, adminUser]);

  // Table Configuration
  const columns = [
    {
      title: "E-mail",
      dataIndex: "email",
      key: "email",
      render: (text, record) => <Text>{text}</Text>,
      align: "left",
      width: "60%",
    },
    {
      title: "Date added",
      dataIndex: "date_joined",
      key: "date_joined",
      render: (text) => <Text>{text}</Text>,
      align: "left",
      width: "30%",
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) => {
        return (
          <Button
            type="text"
            style={{ border: "none", padding: 0 }}
            icon={
              <svg
                width="16"
                height="22"
                viewBox="0 0 5 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.654636 6.12687L0.381753 2.0882H4.40351L4.10312 6.12985C4.08584 6.3624 3.89212 6.54225 3.65894 6.54225H1.09903C0.864692 6.54225 0.670433 6.36067 0.654636 6.12687Z"
                  stroke="#020202"
                  strokeWidth="0.254517"
                />
                <path
                  d="M0.271374 1.99717C0.212126 1.64756 0.437746 1.30964 0.787839 1.2333L3.89625 0.555429C4.24634 0.479082 4.59216 0.692376 4.68386 1.03492L0.271374 1.99717Z"
                  stroke="#020202"
                  strokeWidth="0.190888"
                />
                <path
                  d="M1.63799 1.04839C1.64122 0.860993 1.7724 0.693455 1.96348 0.651785L2.58516 0.516212C2.77624 0.474542 2.96527 0.572251 3.04623 0.741288L1.63799 1.04839Z"
                  stroke="#020202"
                  strokeWidth="0.190888"
                />
                <line
                  x1="1.34905"
                  y1="2.8154"
                  x2="1.55855"
                  y2="5.5462"
                  stroke="#020202"
                  strokeWidth="0.190888"
                  strokeLinecap="round"
                />
                <line
                  x1="0.0954439"
                  y1="-0.0954439"
                  x2="2.83427"
                  y2="-0.0954439"
                  transform="matrix(-0.0764943 0.99707 0.99707 0.0764943 3.55469 2.72461)"
                  stroke="#020202"
                  strokeWidth="0.190888"
                  strokeLinecap="round"
                />
                <line
                  x1="2.40794"
                  y1="2.82005"
                  x2="2.40794"
                  y2="5.55611"
                  stroke="#020202"
                  strokeWidth="0.190888"
                  strokeLinecap="round"
                />
              </svg>
            }
            onClick={() => {
              setUserToDelete(record);
              setIsDeleteModalVisible(true);
            }}
            className="delete-button"
          />
        );
      },
    },
  ];

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    if (!contextTeamMembers) return;

    // First, find current user
    const currentUserData = contextTeamMembers.find(
      (member) => member.email === currentUserEmail,
    );

    // Filter and format other members
    const otherMembers = contextTeamMembers
      .filter((member) => member.email !== currentUserEmail)
      .filter((member) => !member.is_admin) // Filter out admin users for all users
      .filter(
        (member) => member.email && member.email.toLowerCase().includes(value),
      )
      .sort((a, b) => new Date(b.date_joined) - new Date(a.date_joined))
      .map((member) => ({
        key: member.email,
        id: member.email,
        email: member.email,
        date_joined: member.date_joined
          ? new Date(member.date_joined).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "N/A",
        isCurrentUser: false,
      }));

    // Include current user in results if it matches search
    let formattedData = [];
    if (currentUserData && currentUserEmail.toLowerCase().includes(value)) {
      formattedData = [
        {
          key: currentUserEmail,
          id: currentUserEmail,
          email: currentUserEmail,
          date_joined: currentUserData.date_joined
            ? new Date(currentUserData.date_joined).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )
            : "N/A",
          isCurrentUser: true,
        },
        ...otherMembers,
      ];
    } else {
      formattedData = otherMembers;
    }

    setDataSource(formattedData);
  };

  const handleInviteSubmit = async () => {
    setHasSubmitted(true);
    try {
      if (!curFirstName?.trim()) {
        setFirstNameStatus(false);
        message.error("First name is required");
        return;
      }

      // Validate email format
      if (!isValidEmail(curEmail)) {
        setEmailStatus(false);
        message.error("Please enter a valid email address");
        return;
      }

      // Last name is not required

      if (!companyId) {
        message.error("Company ID is missing");
        return;
      }

      // Send invitation
      message.loading({ content: "Inviting user...", key: "inviteUser" });
      const response = await inviteUser({
        email: curEmail.trim(),
        first_name: curFirstName.trim(),
        last_name: curLastName.trim(),
        company_id: companyId,
      });

      if (response.result) {
        handleInviteSuccess(response);
      } else {
        handleInviteError(response);
      }
    } catch (error) {
      message.error({
        content: "Failed to invite user. Please try again.",
        key: "inviteUser",
        duration: 3,
      });
    }
  };

  const handleInviteSuccess = async (response) => {
    message.success({
      content: response.message,
      key: "inviteUser",
      duration: 2,
    });
    setShowSuccessAlert(true);
    setSuccessMessage(response.message);

    // Reset form
    resetForm();

    // Refresh data
    if (context.refreshTeamMembers) {
      await context.refreshTeamMembers();
    }
  };

  const handleInviteError = (response) => {
    message.error({
      content: response.error,
      key: "inviteUser",
      duration: 3,
    });

    if (response.error?.includes("already exists")) {
      setEmailStatus(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setLoading(true);
    try {
      const response = await deleteUser({
        email: userToDelete.email,
        company_id: companyId,
      });

      if (response.result) {
        message.success("User deleted successfully");

        // Handle self-deletion
        if (userToDelete.email === currentUserEmail) {
          // Clear all local storage and auth data
          localStorageService.clearUserData();
          // Small delay to ensure message is shown before redirect
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
        } else {
          // Only update the local state if we're not deleting ourselves
          setDataSource((prevData) =>
            prevData.filter((item) => item.email !== userToDelete.email),
          );
        }
      } else {
        message.error(response.error || "Failed to delete user");
      }
    } catch (error) {
      message.error(`Failed to delete user: ${error.message}`);
    } finally {
      setLoading(false);
      setIsDeleteModalVisible(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setUserToDelete(null);
  };

  // Add confirmation text for self-deletion
  const getDeleteModalContent = () => {
    if (userToDelete?.email === currentUserEmail) {
      return (
        <>
          <p>
            Are you sure you want to delete your own account? This action cannot
            be undone.
          </p>
          <p>You will be logged out and redirected to the login page.</p>
        </>
      );
    }
    return <p>Are you sure you want to delete this user?</p>;
  };

  // UI Handlers
  const showSecondDrawer = () => setSecondDrawerVisible(true);
  const closeSecondDrawer = () => {
    setSecondDrawerVisible(false);
    resetForm();
    setShowSuccessAlert(false);
  };

  // Helper Functions
  const resetForm = () => {
    setCurEmail("");
    setCurFirstName("");
    setCurLastName("");
    setEmailStatus(true);
    setFirstNameStatus(true);
    setLastNameStatus(true);
    setHasSubmitted(false);
  };

  // Add state for success message
  const [successMessage, setSuccessMessage] = useState("");

  // Render
  return (
    <div
      className="team-members-details"
      style={{ padding: "1rem 1.8rem 0 .25rem" }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Header */}
        <Space direction="vertical" size={25}>
          <Space direction="vertical" size={16}>
            <h2 className="header-letter-spacing">User management</h2>
            <p>Manage and invite portal users.</p>
          </Space>

          {/* Search and Invite Button */}
          <div className="search-container">
            <Input
              placeholder="search"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              className="team-members-search"
            />
            <Button
              type="default"
              onClick={showSecondDrawer}
              // className="team-members-invite-button"
            >
              + invite user
            </Button>
          </div>
        </Space>

        {/* Users Table */}
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowClassName="highlight-bottom-border custom-row-height"
          className="team-members-table"
          locale={{ emptyText: "No team members found" }}
        />
      </Space>

      {/* Invite User Drawer */}
      <Drawer
        placement={defaultPlacement}
        onClose={closeSecondDrawer}
        open={secondDrawerVisible}
        closable={true}
        maskClosable={true}
        rootClassName="invite-drawer"
        className="custom-drawer-header"
      >
        <Space direction="vertical">
          {/* Drawer Header */}
          <Space direction="vertical">
            <Space
              direction="vertical"
              className="profile-details-section-header"
            >
              <h2 className="header-letter-spacing">Invite user</h2>
              <Link
                className="modal-guide-banner"
                target="_blank"
                to={UserManagementDocsLink}
              >
                <div className="file-icon">
                  <svg
                    width="30"
                    height="36"
                    viewBox="0 0 30 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g filter="url(#filter0_d_190_157)">
                      <path
                        d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                        fill="#69C4FF"
                        fillOpacity="0.5"
                      />
                      <path
                        d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                        fill="#69C4FF"
                        fillOpacity="0.5"
                      />
                      <path
                        d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                        fill="#DBF1FF"
                      />
                      <path
                        d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                        fill="#B0E0FF"
                      />
                      <path
                        d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                        fill="white"
                      />
                      <line
                        x1="8.82252"
                        y1="9.40248"
                        x2="19.988"
                        y2="9.40248"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.90306"
                        y1="15.1451"
                        x2="20.0685"
                        y2="15.1451"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.82252"
                        y1="11.3499"
                        x2="19.988"
                        y2="11.3499"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.90306"
                        y1="17.0925"
                        x2="20.0685"
                        y2="17.0925"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.82252"
                        y1="13.1675"
                        x2="19.988"
                        y2="13.1675"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <path
                        d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                        fill="#69C4FF"
                        fillOpacity="0.14"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_190_157"
                        x="0.255097"
                        y="0.170166"
                        width="24.9498"
                        height="30.5854"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dy="1.74496" />
                        <feGaussianBlur stdDeviation="0.872482" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                        />
                        <feBlend
                          mode="normal"
                          in2="BackgroundImageFix"
                          result="effect1_dropShadow_190_157"
                        />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow_190_157"
                          result="shape"
                        />
                      </filter>
                    </defs>
                  </svg>
                </div>
                <div className="panel-button-text">
                  <div className="panel-button-desc">
                    Click here to follow the complete setup guide for the Google
                    Cloud Run.
                    <br />
                    Question not covered?{" "}
                    <SupportRequestLink />
                  </div>
                </div>
              </Link>
              <p>
                Fill in the fields below to invite a new user to the
                Code-Cube.io portal.
              </p>
            </Space>

            {/* Success Alert */}
            {showSuccessAlert && (
              <Space direction="vertical" size={50}>
                <div className="custom-alert">
                  <span className="alert-icon">📨</span>
                  <div className="alert-content">
                    <div className="alert-message">Invitation sent!</div>
                    <div className="alert-description">
                      {successMessage === "User successfully added to company"
                        ? "The user has been added to your company. They will receive an email with instructions to access the portal."
                        : "You have successfully invited the user. They will receive an email with further instructions to create an account."}
                    </div>
                  </div>
                </div>
              </Space>
            )}

            {/* Invite Form */}
            <Space direction="vertical" size="middle">
              <Space
                direction="horizontal"
                size={50}
                style={{ columnGap: "1.5rem" }}
                className="invite-name-fields"
              >
                <Space direction="vertical" size="small" style={{ flex: 1 }}>
                  <span>
                    <Text strong>First name</Text> *
                  </span>
                  <Input
                    value={curFirstName}
                    onChange={(e) => setCurFirstName(e.target.value)}
                    status={firstNameStatus ? "" : "error"}
                    style={{ width: "19rem" }}
                  />
                </Space>
                <Space direction="vertical" size="small" style={{ flex: 1 }}>
                  <Text strong>Last name</Text>
                  <Input
                    value={curLastName}
                    onChange={(e) => setCurLastName(e.target.value)}
                    style={{ width: "19rem" }}
                  />
                </Space>
              </Space>

              <Space direction="vertical" size="small">
                <span>
                  <Text strong>Email</Text> *
                </span>
                <Text>
                  Add your personal email address, this email will also be used
                  as your username in the portal.
                </Text>
                <Space.Compact size="middle">
                  <Input
                    placeholder="hello@code-cube.io"
                    value={curEmail}
                    type="email"
                    onChange={(e) => setCurEmail(e.target.value)}
                    style={{ width: "19rem" }}
                  />
                </Space.Compact>
              </Space>
            </Space>
          </Space>

          {/* Submit Button */}
          <Space
            direction="vertical"
            size="large"
            style={{ width: "100%", marginTop: "20px" }}
          >
            <Popover
              content={
                !isFormValid
                  ? "Please fill in all required fields to proceed"
                  : null
              }
              trigger="hover"
            >
              <Button
                type="default"
                onClick={handleInviteSubmit}
                disabled={!isFormValid}
                className={`${isFormValid ? "enabled" : "disabled"}`}
              >
                invite
              </Button>
            </Popover>
          </Space>
        </Space>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        centered
        title="Delete user"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          type: "primary",
          danger: userToDelete?.email === currentUserEmail,
        }}
        cancelButtonProps={{ type: "default" }}
      >
        {getDeleteModalContent()}
      </Modal>
    </div>
  );
};
